import React, { useState, useEffect, useRef, useCallback } from "react"
import { Typography, Grid, Card, useMediaQuery } from "@mui/material"
import { EmployeeTableKey, getEmployeeTableColumns } from "./columns"
import ReconciliationTable from "../../../../components/Tables/ReconciliationTable"
import { useMemo } from "react"
import { useVariableData } from "../../../../context/VariableDataContext"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs from "dayjs"
import { motion } from "framer-motion"
import DoubleArrowIcon from "@mui/icons-material/KeyboardDoubleArrowDown"
import { useDemo } from "../../../../context/DemoContext"
import DemoCTA from "../../../../components/DemoCTA"

import "./index.css"
import LoadingScreen from "../../../../LoadingScreen"
import EmployeeBreakDownModal from "./EmployeeBreakDownModal"

const rowHeight = 36

const currentDayjs = dayjs()

function EmployeeList() {
  const { client } = useVariableData()
  const { hasDemoAccess, demoData } = useDemo() // ✅ add this
  const [showDemoCTA, setShowDemoCTA] = useState(false)

  const [employeeList, setEmployeeList] = React.useState(null)

  const [selectedMonth, setSelectedMonth] = useState(currentDayjs)
  const [selectedYear, setSelectedYear] = useState(currentDayjs)
  const [availableMonths, setAvailableMonths] = useState([])

  const [modalInfo, setModalInfo] = React.useState({
    isModalLoading: false,
    selectedRow: null,
    modalTableColumns: null,
    modalTableTitle: null,
    modalTableKey: null,
    openModal: false,
    detailRows: [],
    totalIncentives: null,
  })

  const isMobile = useMediaQuery("(max-width:500px)")

  const tableExtras = isMobile ? 200 : 100 // padding above the table

  const defaultHeight = isMobile ? 500 : 351

  const [cardHeight, setCardHeight] = useState(defaultHeight) // default height

  const cardRef = useRef(null)

  useEffect(() => {
    // ------------------------
    // DEMO MODE
    // ------------------------
    if (hasDemoAccess && demoData) {
      setAvailableMonths(demoData.employeeAvailableMonths || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12])

      const month = selectedMonth?.month() + 1
      const year = selectedYear?.year()

      let data = demoData.incentiveByMonth

      if (!data) return

      setEmployeeList(data.records || [])

      return
    }
  }, [hasDemoAccess, demoData, selectedYear, selectedMonth])

  // useEffect(() => {
  //   if (client) {
  //     clientInfoSetter(client);
  //   }
  // }, [client]);

  const handleClose = () => {
    setModalInfo({
      isModalLoading: false,
      selectedRow: null,
      modalTableColumns: null,
      modalTableTitle: null,
      modalTableKey: null,
      openModal: false,
      detailRows: [],
      totalIncentives: null,
    })
  }

  const handleYearChange = (newYear) => {
    setShowDemoCTA(true)
  }

  const handleAppealRowClick = (row) => {}

  const getRowClassName = (row) => {
    return ""
  }

  const handleBreakdownClick = useCallback(() => {
    setShowDemoCTA(true)
  })

  const EmployeeTableColumns = useMemo(() => {
    return getEmployeeTableColumns(handleBreakdownClick)
  }, [handleBreakdownClick])

  return (
    <>
      {!hasDemoAccess && (
        <LoadingScreen
          h1={"Loading Employee Incentives..."}
          style={{
            position: "fixed",
            width: "100%",
            left: 0,
            top: 0,
            backgroundColor: "rgb(0,0,0,0.5)",
            color: "#fff",
          }}
        />
      )}

      <DemoCTA open={showDemoCTA} onClose={() => setShowDemoCTA(false)} />

      <EmployeeBreakDownModal
        modalInfo={modalInfo}
        employeeId={modalInfo?.selectedRow?.id}
        clientId={client}
        month={selectedMonth}
        year={selectedYear}
        isModal={true}
        open={modalInfo.openModal}
        onClose={handleClose}
        backOffice={false}
      />

      <Card
        ref={cardRef}
        style={{
          position: "relative",
          padding: "10px 20px 0px",
          height: `${cardHeight}px`,
          transition: `${cardHeight == defaultHeight ? "0.4s all ease" : "0s all ease"}`,
          paddingBottom: "25px",
        }}
        //       sx={{
        //   position: "relative",
        //   padding: "20px 24px 40px 24px",
        //   height: `${cardHeight}px`,
        //   transition: "height 0.5s ease",
        //   borderRadius: 3,
        //   background: "rgba(255,255,255,0.08)",
        //   backdropFilter: "blur(12px)",
        //   boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        //   border: "1px solid rgba(255,255,255,0.18)",
        //   overflow: "hidden",
        // }}
        className="va-card-3"
        onDoubleClick={
          // handleExpand
          () => {
            if (!employeeList) return

            setCardHeight((prevHeight) => {
              const expandedHeight = rowHeight * employeeList.length + tableExtras
              const isExpanding = prevHeight === defaultHeight
              const newHeight = isExpanding ? expandedHeight : defaultHeight

              // Scroll into view *after* state update
              setTimeout(() => {
                if (isExpanding && cardRef.current) {
                  cardRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "end",
                  })
                }
              }, 100)

              return newHeight
            })
          }
        }
      >
        <div
          style={{
            marginBottom: "10px",
            display: "flex",
            width: "100%",
            flexWrap: "wrap",

            gap: 20,
          }}
        >
          <div style={{ flex: 1 }}>
            <Typography
              variant="h4"
              className="va-heading-1"
              sx={{
                fontWeight: 700,
                color: "#ffffff",
                textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                letterSpacing: "0.5px",
              }}
            >
              Employee Commission
            </Typography>
          </div>
          <div
            style={{
              display: "flex",
              gap: "20px",
              height: "max-content",
              marginTop: "5px",
              flexWrap: "wrap",
            }}
          >
            <DatePicker
              label="Month"
              // views={["month"]}
              views={["month"]}
              value={selectedMonth}
              onChange={(newMonth) => setShowDemoCTA(true)}
              shouldDisableMonth={(date) => {
                const month = date.month() + 1 // 0-based to 1-based
                return !availableMonths.includes(month)
              }}
              disabled={!availableMonths.length}
              slotProps={{
                textField: {
                  placeholder: "Select month",
                  variant: "outlined",
                  size: "small",
                  InputProps: {
                    disableUnderline: true,
                  },
                  InputLabelProps: {
                    shrink: true,
                  },
                  sx: {
                    background: "var(--va-primary-bg)",
                    width: 150,
                    border: "0px",

                    "& .MuiDateCalendar-root": {
                      paddingTop: "10px",
                    },

                    "& .MuiInputLabel-root": {
                      // fontSize: "0.75rem",
                      color: "var(--va-text-primary-color)",
                      fontWeight: "bold",
                      fontFamily: "Montserrat, san-serif",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                      color: "var(--va-text-primary-color)",
                    },

                    " & .MuiPickersSectionList-section span": {
                      color: "var(--va-text-primary-color)",
                    },
                    // "& .MuiPickersOutlinedInput-notchedOutline": {
                    //   border: "1px solid var(--va-text-primary-color)",
                    // },
                  },
                },
              }}
            />

            <DatePicker
              label="Year"
              maxDate={currentDayjs}
              views={["year"]}
              value={selectedYear}
              yearsOrder="desc"
              onChange={handleYearChange}
              slotProps={{
                textField: {
                  placeholder: "Select month",
                  variant: "outlined",
                  size: "small",
                  InputProps: {
                    disableUnderline: true,
                  },
                  InputLabelProps: {
                    shrink: true,
                  },
                  sx: {
                    background: "var(--va-primary-bg)",
                    width: 150,
                    "& .MuiInputLabel-root": {
                      // fontSize: "0.75rem",
                      color: "var(--va-text-primary-color)",
                      fontWeight: "bold",
                      fontFamily: "Montserrat, san-serif",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                      color: "var(--va-text-primary-color)",
                    },

                    " & .MuiPickersSectionList-section span": {
                      color: "var(--va-text-primary-color)",
                    },
                    // "& .MuiPickersOutlinedInput-notchedOutline": {
                    //   border: "1px solid var(--va-text-primary-color)",
                    // },
                  },
                },
              }}
              sx={{
                minWidth: 150,
                color: "#cecece",
              }}
            />
          </div>
        </div>

        {employeeList && (
          <div id="Appeal-Detail-Table" style={{ height: "calc(100% - 60px)" }}>
            <Grid sx={{ marginTop: 1, height: "100%" }}>
              {employeeList.length > 0 && (
                <ReconciliationTable
                  maxHeight={"calc(100%)"}
                  getRowClassName={getRowClassName}
                  isAccordion={false}
                  isExpanded={true}
                  columns={EmployeeTableColumns}
                  tableKey={EmployeeTableKey}
                  title={"Appeals Detail Transactions"}
                  rows={employeeList}
                  handleRowClick={handleAppealRowClick}
                />
              )}
            </Grid>
          </div>
        )}

        {cardHeight <= defaultHeight ? (
          <motion.div
            style={{
              position: "absolute",
              bottom: 10,
              left: "50%",
              transform: "translateX(-50%)",
              padding: "8px 16px",
              backdropFilter: "blur(10px)",
              background: "rgba(255, 255, 255, 0.15)",
              borderRadius: "50px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#444",
              pointerEvents: "none",
              fontWeight: 600,
              fontSize: 13,
            }}
            animate={{ y: [0, -5, 0] }}
            transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
          >
            <DoubleArrowIcon fontSize="small" />
            <span>Double click to expand</span>
          </motion.div>
        ) : // (
        //   <motion.div
        //     style={{
        //       position: "absolute",
        //       bottom: 8,
        //       left: "40%",
        //       transform: "translateX(-50%)",
        //       display: "flex",
        //       flexDirection: "column",
        //       alignItems: "center",
        //       color: "#555",
        //       fontSize: 12,
        //       cursor: "pointer",
        //       pointerEvents: "none",
        //     }}
        //     animate={{ y: [0, -5, 0] }}
        //     transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
        //   >
        //     <DoubleArrowIcon fontSize="small" style={{ transform: "rotate(180deg)" }} />
        //     <span>Double click to contract</span>
        //   </motion.div>
        // )
        null}
      </Card>
    </>
  )
}
export default EmployeeList
